/**
 * FlowAI Memory-Browser — 4 Layer (working, episodic, semantic, procedural).
 *
 * @page /flowai/memory
 * @since v2.484.0
 */
import { useEffect, useState } from 'react';
import { PageHeader, Card, Badge } from '../../components/ui';
import { IconCases } from '../../components/Icons';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

const LAYERS = [
  { key: 'working', label: 'Working', empty: 'Working Memory wird in Phase 2 ausgerollt (Playbook-Run-Kontext).' },
  { key: 'episodic', label: 'Episodic', empty: 'Episodic Memory wird automatisch pro Skill-Execution befüllt (B2B-Sends, Customer-Approvals).' },
  { key: 'semantic', label: 'Semantic', empty: 'Semantic Memory wird in Phase 2 ausgerollt (Kunden-Attribute, Präferenzen).' },
  { key: 'procedural', label: 'Procedural', empty: 'Procedural Memory wird in Phase 2 ausgerollt (Skill-Performance-Patterns).' },
];

export default function FlowAIMemory() {
  const [layer, setLayer] = useState('episodic');
  const [rows, setRows] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/flowai/memory/${layer}?limit=100`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : (d?.entries ?? [])))
      .catch(() => setRows([]));
  }, [layer]);

  return (
    <div className="p-6">
      <FlowAIBreadcrumb trail={[{ label: 'Memory-Browser' }]} />
      <PageHeader
        title="Memory-Browser"
        subtitle="Working · Episodic · Semantic · Procedural"
      />

      <div className="border-b border-[var(--border-subtle,#E5E7EB)] mb-4 flex gap-1">
        {LAYERS.map((l) => (
          <button
            key={l.key}
            onClick={() => setLayer(l.key)}
            className={`px-4 py-2 text-sm transition-colors ${
              layer === l.key
                ? 'border-b-2 border-[var(--text-primary,#1A1A2E)] text-[var(--text-primary,#1A1A2E)] font-medium -mb-px'
                : 'text-[var(--text-secondary,#4B5563)] hover:text-[var(--text-primary,#1A1A2E)]'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>

      <Card padding="sm">
        <div className="divide-y divide-[var(--border-subtle,#E5E7EB)] max-h-[70vh] overflow-y-auto">
          {rows.map((r) => (
            <div key={r.id} className="p-3 text-[12px]">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-[var(--text-tertiary,#6B7280)]">#{r.id}</span>
                {r.subject_type && <Badge>{r.subject_type}</Badge>}
                {r.subject_key && <code className="text-[11px]">{r.subject_key}</code>}
                <span className="ml-auto text-[var(--text-tertiary,#6B7280)]">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</span>
              </div>
              <pre className="bg-[var(--bg-inset,#F8F9FC)] p-2 rounded text-[11px] overflow-x-auto">{JSON.stringify(r.payload || r.content || r, null, 2)}</pre>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--bg-inset,#F8F9FC)] mb-3">
                <IconCases className="w-6 h-6 text-[var(--text-tertiary,#6B7280)]" />
              </div>
              <div className="text-sm font-medium mb-1">Memory-Layer {layer} ist leer</div>
              <div className="text-[12px] text-[var(--text-tertiary,#6B7280)]">{LAYERS.find((l) => l.key === layer)?.empty || 'Sobald FlowAI Aktionen ausführt, werden hier Einträge sichtbar.'}</div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
